import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useAuth } from "contexts/authContext";
import CenteredAuth from "layouts/auth/variants/Centered";
import { useState } from "react";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { NavLink, useNavigate } from "react-router-dom";
import { contactEmailSchema, flattenZodErrors, passwordSchema } from "validations/Validations";
import { z } from "zod";

// ---- Sign In Schema ----
const signInSchema = z.object({
  email: contactEmailSchema,
  password: passwordSchema.shape.newPassword,
});

function SignIn() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role, login } = useAuth();

  // Chakra colors
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");

  // States
  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", userRole: "" });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const handleClick = () => setShow(!show);

  // Field-level validation 
  const handleInputChange = (field: "email" | "password", value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    const fieldSchema = signInSchema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      setErrors((prev) => ({
        ...prev,
        [field]: result.success ? "" : result.error.issues[0].message,
      }));
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (isLoading) return; // prevent multiple clicks

    setIsLoading(true);

    try {
      setErrors({});
      await login(formData);

      const userRole = role || localStorage.getItem("role");

      if (userRole === "Admin") {
        navigate("/admin/dashboards");
      } else if (userRole === "Vendor" || userRole === "VendorUser") {
        navigate("/admin/vendor-dashboard");
      } else if (userRole === "DeptUser") {
        navigate("/admin/deptuserDashboard");
      } else if (userRole === "OrgAdmin" || userRole === "LeasedOrg") {
        navigate("/admin/orgadminDashboard");
      } else if (userRole === "LocationAdmin") {
        navigate("/admin/locadminDashboard");
      }

      toast({
        title: "Login successful",
        description: `Welcome back, ${userRole || "User"}!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

    } catch (error: any) {
      if (error instanceof z.ZodError) {
        const newErrors = flattenZodErrors(error);
        setErrors(newErrors);
      } else {

        const backendMessage =
          typeof error?.response?.data === "string"
            ? error.response.data
            : error?.response?.data?.message ||
            "Invalid email or password. Please try again.";

        toast({
          title: "Login failed",
          description: backendMessage,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <CenteredAuth cardTop={{ base: "140px", md: "14vh" }} cardBottom={{ base: "50px", lg: "auto" }} mx="0px">
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        justifyContent="center"
        px={{ base: "20px", md: "0px" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Sign In
          </Heading>
          <Text mb="36px" ms="4px" color={textColorSecondary} fontWeight="400" fontSize="md">
            Enter your email and password to sign in!
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          {/* Email */}
          <FormControl isInvalid={!!errors.email}>
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
              display="inline-flex"
              alignItems="center"
            >
              Email<Text as="span" color={brandStars} ms="1"> *</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="email"
              placeholder="mail@simmmple.com"
              mb="8px"
              fontWeight="500"
              size="lg"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
            {errors.email && <FormErrorMessage mb="16px">{errors.email}</FormErrorMessage>}
          </FormControl>

          {/* Password */}
          <FormControl /* isInvalid={!!errors.password} */>
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
              display="inline-flex"
              alignItems="center"
            >
              Password<Text as="span" color={brandStars} ms="1"> *</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                isRequired
                fontSize="sm"
                placeholder="Min. 8 characters"
                mb="8px"
                size="lg"
                type={show ? "text" : "password"}
                variant="auth"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            {/* {errors.password && <FormErrorMessage mb="16px">{errors.password}</FormErrorMessage>} */}
          </FormControl>

          {/* Remember + Forgot */}
          <Flex justifyContent="space-between" align="center" mb="24px">
            <FormControl display="flex" alignItems="center">
              <Checkbox id="remember-login" colorScheme="brand" me="10px" />
              <FormLabel htmlFor="remember-login" mb="0" fontWeight="normal" color={textColor} fontSize="sm">
                Keep me logged in
              </FormLabel>
            </FormControl>
            <NavLink to="/auth/forgot-password">
              <Text color={textColorBrand} fontSize="sm" w="124px" fontWeight="500">
                Forgot password?
              </Text>
            </NavLink>
          </Flex>

          {/* Submit */}
          <Button
            type="button"
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%" h="50"
            mb="24px"
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={handleSubmit}>
            Sign In
          </Button>

          {/* Sign up */}
          {/* <Flex flexDirection="column" justifyContent="center" alignItems="start" maxW="100%" mt="0px">
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Not registered yet?
              <NavLink to="/auth/sign-up">
                <Text color={textColorBrand} as="span" ms="5px" fontWeight="500">
                  Create an Account
                </Text>
              </NavLink>
            </Text>
          </Flex> */}
        </Flex>
      </Flex>
    </CenteredAuth>
  );
}

export default SignIn;
